import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import { useTheme } from "@mui/material";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  NodeKey,
} from "lexical";
import React, { JSX, useCallback, useEffect, useRef, useState } from "react";
import { $isImageNode } from "./ImageNode";
import { ImageResizer } from "./ImageResizer";

interface ImageComponentProps {
  altText: string;
  height: "inherit" | number;
  nodeKey: NodeKey;
  src: string;
  width: "inherit" | number;
}

/**
 * Renders the embedded image and, when the node is selected in an editable
 * editor, overlays drag handles so the image can be resized.
 */
export function ImageComponent({ src, altText, nodeKey, width, height }: ImageComponentProps): JSX.Element {
  const theme = useTheme();
  const [editor] = useLexicalComposerContext();
  const imageRef = useRef<HTMLImageElement>(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [isResizing, setIsResizing] = useState(false);

  const onDelete = useCallback(
    (event: KeyboardEvent) => {
      const selection = $getSelection();
      if (isSelected && $isNodeSelection(selection)) {
        event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) {
          node.remove();
        }
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (isResizing) {
        return true;
      }
      if (event.target === imageRef.current) {
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }
      return false;
    },
    [clearSelection, isResizing, isSelected, setSelected]
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand<MouseEvent>(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
        editor.registerCommand<DragEvent>(
          DRAGSTART_COMMAND,
          (event) => {
            if (event.target === imageRef.current) {
              event.preventDefault();
              return true;
            }
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
      ),
    [editor, onClick, onDelete]
  );

  const onResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  const onResizeEnd = useCallback(
    (nextWidth: number, nextHeight: number) => {
      // Delay clearing so the trailing click from the drag does not deselect.
      setTimeout(() => setIsResizing(false), 200);
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) {
          node.setWidthAndHeight(nextWidth, nextHeight);
        }
      });
    },
    [editor, nodeKey]
  );

  const showResizer = isSelected && editor.isEditable();

  return (
    <span style={{ display: "inline-block", lineHeight: 0, position: "relative" }}>
      <img
        ref={imageRef}
        alt={altText}
        draggable={false}
        src={src}
        style={{
          display: "block",
          height: height === "inherit" ? "inherit" : height,
          maxWidth: "100%",
          outline: isSelected ? `2px solid ${theme.palette.info.light}` : "none",
          width: width === "inherit" ? "inherit" : width,
        }}
      />
      {showResizer && (
        <ImageResizer editor={editor} imageRef={imageRef} onResizeEnd={onResizeEnd} onResizeStart={onResizeStart} />
      )}
    </span>
  );
}
